import { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Pagination } from "@mui/material";
import ItemFilters from "../components/ItemFilters";
import ItemGrid from "../components/ItemGrid";
import SortFilterBar from "../components/SortFilterBar";
import { SORT_OPTIONS, PAGE_SIZES } from "../components/ItemListToolbar";
import { CatalogController } from "../controllers/catalog";
import { applyFilters, applySort, deriveFacets } from "../lib/itemFacets";
import { useCurrency } from "../context/CurrencyContext";
import styles from "./ItemListPage.module.css";

function countActiveFilters(filters, facets) {
  if (!filters) return 0;
  let n = 0;
  if (filters.priceRange &&
      (filters.priceRange[0] > facets.price.min || filters.priceRange[1] < facets.price.max)) n++;
  if (filters.volumeRange &&
      (filters.volumeRange[0] > facets.volume.min || filters.volumeRange[1] < facets.volume.max)) n++;
  if (filters.colors?.length) n += filters.colors.length;
  if (filters.intendedUse?.length) n += filters.intendedUse.length;
  if (filters.frameSystem?.length) n += filters.frameSystem.length;
  return n;
}

export default function ItemListPage({ productLine, defaultSort = "relevance" }) {
  const { currency } = useCurrency();
  const [items, setItems] = useState([]);
  const [serverFacets, setServerFacets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState(defaultSort);
  const [pageSize, setPageSize] = useState(48);
  const [page, setPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    CatalogController.list({ productLine: productLine || "", currency })
      .then((res) => {
        if (cancelled) return;
        const list = res?.items ?? res ?? [];
        setItems(list);
        setServerFacets(res?.facets || null);
      })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [productLine, currency]);

  const facets = useMemo(
    () => serverFacets || deriveFacets(items, currency),
    [serverFacets, items, currency]
  );

  const filtered = useMemo(() => applyFilters(items, filters, currency), [items, filters, currency]);
  const sorted = useMemo(() => applySort(filtered, sort, currency), [filtered, sort, currency]);
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const visible = useMemo(
    () => sorted.slice((page - 1) * pageSize, page * pageSize),
    [sorted, page, pageSize]
  );
  const activeFilterCount = useMemo(() => countActiveFilters(filters, facets), [filters, facets]);

  // Reset page on filter / sort / size change.
  useEffect(() => { setPage(1); }, [filters, sort, pageSize]);

  return (
    <div className={styles.page}>
      <div className={styles.layoutWrap}>
      <div className={styles.layout}>
        <aside className={styles.sidebar}>
          <ItemFilters
            facets={facets}
            value={filters}
            onChange={setFilters}
            onClear={() => setFilters({})}
          />
        </aside>
        <main className={styles.main}>
          <SortFilterBar
            count={sorted.length}
            pageSize={pageSize}
            onPageSize={setPageSize}
            pageSizes={PAGE_SIZES}
            sort={sort}
            onSort={setSort}
            sortOptions={SORT_OPTIONS}
            filtersNode={
              <ItemFilters
                facets={facets}
                value={filters}
                onChange={setFilters}
                onClear={() => setFilters({})}
              />
            }
            onClearFilters={() => setFilters({})}
            activeFilterCount={activeFilterCount}
          />
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : sorted.length === 0 ? (
            <Box sx={{ py: 8, textAlign: "center", color: "text.secondary" }}>
              No items match the current filters.
            </Box>
          ) : (
            <>
              <ItemGrid items={visible} />
              {totalPages > 1 && (
                <div className={styles.pager}>
                  <Pagination
                    page={page}
                    count={totalPages}
                    onChange={(_, p) => setPage(p)}
                    shape="rounded"
                  />
                </div>
              )}
            </>
          )}
        </main>
      </div>
      </div>
    </div>
  );
}
